import View from '@play-co/timestep-core/lib/ui/View';
import { ABTestBucket, ABTestID } from 'src/replicant/ruleset/ab';
import { AB } from 'src/lib/AB';
import { ScreenId } from '../../logic/ScreenManager';

export type Popup<TOpenArgs extends {} = {}, TCloseArgs extends any[] = []> = {
  SCREEN?: ScreenId;
} & ((opts: {
  superview: View;
  openArgs: TOpenArgs;
  close: (...closeArgs: TCloseArgs) => void;
}) => {
  onClose: (...closeArgs: TCloseArgs) => void;
});

type PopupCtor<TOpenArgs extends {}, TCloseArgs extends any[]> = new (opts: {
  superview: View;
  close: (...closeArgs: TCloseArgs) => void;
}) => {
  init: (openArgs: TOpenArgs) => void;
  fadeOut: () => void;
  onPopupClosing?: (() => void) | ((...closeArgs: TCloseArgs) => void);
};

export function popupBoilerplate<
  TOpenArgs extends {},
  TCloseArgs extends any[],
>(
  PopupCtor: PopupCtor<TOpenArgs, TCloseArgs>,
  screen?: ScreenId,
): Popup<TOpenArgs, TCloseArgs> {
  const popupFactory: Popup<TOpenArgs, TCloseArgs> = ({
    superview,
    openArgs,
    close,
  }) => {
    const popup = new PopupCtor({ superview, close });

    popup.init(openArgs);

    return {
      onClose: (...closeArgs) => {
        popup.onPopupClosing?.(...closeArgs);
        popup.fadeOut();
      },
    };
  };

  popupFactory.SCREEN = screen;

  return popupFactory;
}

export function abTestPopup<
  TABTestID extends ABTestID,
  TOpenArgs extends {},
  TCloseArgs extends any[],
>(
  testId: TABTestID,
  buckets: Partial<
    Record<ABTestBucket<TABTestID>, Popup<TOpenArgs, TCloseArgs>>
  >,
  fallback: Popup<TOpenArgs, TCloseArgs>,
): Popup<TOpenArgs, TCloseArgs> {
  return (opts) => {
    const bucket = AB.getBucketID(testId);
    const popup = buckets[bucket] || fallback;
    return popup(opts);
  };
}
